import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Play } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';
import SceneLayout from '@/components/scene/SceneLayout';
import Aperture from '@/components/scene/Aperture';
import Poche from '@/components/scene/Poche';
import { useActiveIndex } from '@/components/scene/useActiveIndex';
import { stepOf } from '@/components/scene/format';
import routeTriptych from '@/assets/scene/how-route-triptych.webp';
import transom from '@/assets/scene/home-transom.webp';
import stopContacts from '@/assets/scene/how-stop-contacts.webp';
import stopCampaign from '@/assets/scene/how-stop-campaign.webp';
import stopLaunch from '@/assets/scene/how-stop-launch.webp';

const STOP_IMAGES = [stopContacts, stopCampaign, stopLaunch];

const FEATURES = ['f1', 'f2', 'f3', 'f4', 'f5', 'f6', 'f7'] as const;
const METRICS = [
  { key: 'm1', value: '75%' },
  { key: 'm2', value: '3X' },
  { key: 'm3', value: '2X' },
  { key: 'm4', value: '30d' },
] as const;

export default function HowItWorks() {
  const { t } = useTranslation();
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoForm, setDemoForm] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const { active, register } = useActiveIndex<HTMLElement>();

  // Home's "Book a demo" links here with #book-demo; React Router doesn't scroll to hashes itself.
  useEffect(() => {
    if (window.location.hash === '#book-demo') {
      document.getElementById('book-demo')?.scrollIntoView({ block: 'start' });
    }
  }, []);

  const handleDemoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDemoLoading(true);
    try {
      await apiFetch('/contact', {
        method: 'POST',
        body: JSON.stringify({ type: 'demo', ...demoForm }),
      });
      toast.success(t('how_it_works.demo_success'));
      setDemoForm({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('how_it_works.demo_error'));
    } finally {
      setDemoLoading(false);
    }
  };

  const steps = [1, 2, 3].map((n) => ({
    n,
    title: t(`how_it_works.step${n}_title`),
    desc: t(`how_it_works.step${n}_desc`),
    fig: t(`how_it_works.fig_stop${n}`),
    img: STOP_IMAGES[n - 1],
    alt: t(`how_it_works.stop${n}_alt`),
  }));

  const [lead, ...footnotes] = METRICS;
  const field = (key: keyof typeof demoForm) => ({
    value: demoForm[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setDemoForm((f) => ({ ...f, [key]: e.target.value })),
  });

  return (
    <SceneLayout>
      {/* Prologue */}
      <section className="wrap pt-[clamp(48px,12vh,140px)] pb-[clamp(32px,6vh,72px)]" data-rv="dissolve">
        <p className="mono mb-5">{t('how_it_works.badge')}</p>
        <h1 className="display max-w-[14ch] mb-6">{t('how_it_works.title')}</h1>
        <p className="body max-w-[48ch]">{t('how_it_works.subtitle')}</p>
        {/* Establishing shot: the whole route seen at once before the stops take it room by room */}
        <Aperture ticks className="mt-[clamp(40px,7vh,80px)]" label={t('how_it_works.establishing_label')}>
          <Poche ratio="16 / 9" src={routeTriptych} alt={t('how_it_works.route_alt')} eager />
        </Aperture>
      </section>

      {/* Tour map — a pinned route, stops passing in their own frames */}
      <section className="wrap pb-[var(--section)]">
        <div className="tour">
          <nav className="route" aria-label={t('how_it_works.route_label')}>
            <p className="mono mb-8">{t('how_it_works.route_label')}</p>
            <ol>
              {steps.map((s, i) => (
                <li key={s.n} data-active={active === i}>
                  <span className="mono block mb-1" style={{ color: 'inherit' }}>{String(s.n).padStart(2, '0')}</span>
                  <span className="text-[17px] font-medium">{s.title}</span>
                </li>
              ))}
            </ol>
          </nav>
          <div data-rv="cut">
            {steps.map((s, i) => (
              <article key={s.n} ref={register(i)} className="stop" data-i style={{ ['--i' as string]: i }}>
                <Aperture label={stepOf(s.n, steps.length)}>
                  <Poche ratio="16 / 9" src={s.img} alt={s.alt} caption={s.fig} />
                </Aperture>
                <h2 className="display-m" style={{ fontSize: 'clamp(1.6rem, 2.6vw, 2.4rem)' }}>{s.title}</h2>
                <p className="body max-w-[44ch]">{s.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* The screening */}
      <section>
        <div className="wrap pb-10 grid gap-4">
          <h2 className="display-m">{t('how_it_works.video_title')}</h2>
          <p className="body max-w-[52ch]">{t('how_it_works.video_description')}</p>
        </div>
        <div className="letterbox">
          <div className="wrap">
            <div className="letterbox__frame" data-rv="aperture">
              {videoPlaying ? (
                <video className="absolute inset-0 w-full h-full" src="/Demo.mp4" controls autoPlay playsInline>
                  {t('how_it_works.video_fallback')}
                </video>
              ) : (
                <Poche tone="dusk" className="absolute inset-0">
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 p-6 text-center text-[var(--paper)]">
                    <button
                      type="button"
                      className="play"
                      onClick={() => setVideoPlaying(true)}
                      aria-label={t('how_it_works.video_cta')}
                    >
                      <Play className="h-7 w-7" fill="currentColor" />
                    </button>
                    <div className="grid gap-2">
                      <p className="text-[clamp(1.25rem,2.4vw,1.75rem)] font-light">{t('how_it_works.video_cta')}</p>
                      <p className="hidden sm:block text-[15px] opacity-75 max-w-[46ch]">{t('how_it_works.video_cta_sub')}</p>
                    </div>
                    <p className="mono hidden sm:block" style={{ color: 'rgba(243,240,232,.7)' }}>{t('how_it_works.video_points')}</p>
                  </div>
                </Poche>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* One number, three footnotes */}
      <section className="wrap section" data-rv="rack">
        <p className="mono mb-8">{t('how_it_works.results_title')} · {t('how_it_works.results_sub')}</p>
        <div className="flex flex-wrap items-end gap-x-14 gap-y-6">
          <p className="giant" dir="ltr">{lead.value}</p>
          <div className="grid gap-2 pb-4 max-w-[28ch]">
            <p className="text-[1.25rem] font-medium">{t(`how_it_works.metrics.${lead.key}.label`)}</p>
            <p className="body">{t(`how_it_works.metrics.${lead.key}.desc`)}</p>
          </div>
        </div>
        <ol className="mt-12 pt-6 border-t border-[var(--hairline-strong)] grid sm:grid-cols-3 gap-6">
          {footnotes.map((m, i) => (
            <li key={m.key} className="grid grid-cols-[1.5rem_1fr] gap-2 text-[15px]">
              <span className="mono">{i + 1}</span>
              <span>
                <b className="font-medium" dir="ltr">{m.value}</b>{' '}
                {t(`how_it_works.metrics.${m.key}.label`)} — <span className="text-[var(--graphite)]">{t(`how_it_works.metrics.${m.key}.desc`)}</span>
              </span>
            </li>
          ))}
        </ol>
      </section>

      {/* Feature schedule */}
      <section className="wrap pb-[var(--section)]">
        <div className="split-golden">
          <div className="sticky-col grid gap-4">
            <p className="mono">S-03</p>
            <h2 className="display-m">{t('how_it_works.features_title')}</h2>
            <p className="body max-w-[34ch]">{t('how_it_works.features_sub')}</p>
          </div>
          <div data-rv="line">
            <div className="ledger__head" />
            {FEATURES.map((f, i) => {
              const benefits = t(`how_it_works.features.${f}.benefits`, { returnObjects: true }) as string[];
              return (
                <div key={f} className="relative">
                  <details className="fx">
                    <summary>
                      <span className="mono">{String(i + 1).padStart(2, '0')}</span>
                      <span className="text-[17px] font-medium">{t(`how_it_works.features.${f}.title`)}</span>
                      <span className="fx__plus" aria-hidden="true">+</span>
                    </summary>
                    <div className="fx__body">
                      <p className="body text-[15px]">{t(`how_it_works.features.${f}.desc`)}</p>
                      <ul className="dash-list">
                        {Array.isArray(benefits) && benefits.map((b) => <li key={b}>{b}</li>)}
                      </ul>
                    </div>
                  </details>
                  {/* outside <details>: closed details hide every child except the summary */}
                  <span className="rv-line" style={{ ['--i' as string]: i }} />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Reception desk — the demo form, inside a doorway */}
      <section id="book-demo" className="wrap pb-[var(--section)] scroll-mt-24">
        <Aperture ticks className="door door--wide" rv="up" label={t('how_it_works.desk_label')}>
          <Poche src={transom} className="door__transom" />
          <div className="door__body">
            <div className="grid gap-3">
              <h2 className="display-m">{t('how_it_works.cta_title')}</h2>
              <p className="body">{t('how_it_works.cta_subtitle')}</p>
            </div>
            <form onSubmit={handleDemoSubmit} className="grid gap-7">
              <div className="grid md:grid-cols-2 gap-7">
                <label className="field" htmlFor="demo-name">
                  <span className="mono">{t('how_it_works.form.name')}</span>
                  <input id="demo-name" placeholder={t('how_it_works.form.name_ph')} required {...field('name')} />
                </label>
                <label className="field" htmlFor="demo-email">
                  <span className="mono">{t('how_it_works.form.email')}</span>
                  <input id="demo-email" type="email" placeholder="your@email.com" required {...field('email')} />
                </label>
              </div>
              <div className="grid md:grid-cols-2 gap-7">
                <label className="field" htmlFor="demo-phone">
                  <span className="mono">{t('how_it_works.form.phone')}</span>
                  <input id="demo-phone" type="tel" dir="ltr" placeholder="+20 xxx xxx xxxx" {...field('phone')} />
                </label>
                <label className="field" htmlFor="demo-company">
                  <span className="mono">{t('how_it_works.form.company')}</span>
                  <input id="demo-company" placeholder={t('how_it_works.form.company_ph')} {...field('company')} />
                </label>
              </div>
              <label className="field" htmlFor="demo-message">
                <span className="mono">{t('how_it_works.form.message')}</span>
                <textarea id="demo-message" rows={4} placeholder={t('how_it_works.form.message_ph')} required {...field('message')} />
              </label>
              <button type="submit" className="btn-ink w-full" disabled={demoLoading}>
                {demoLoading ? t('how_it_works.form.submitting') : t('how_it_works.form.submit')}
                {!demoLoading && <ArrowRight className="btn-arrow h-4 w-4" />}
              </button>
            </form>
          </div>
        </Aperture>
      </section>
    </SceneLayout>
  );
}
